<template>
  <div class="home min-h-screen">

    <!-- Hero Section with Vanta animation -->
    <div class="hero-container relative overflow-hidden" style="height: 100vh;">
      <!-- Vanta background container that's specific to the hero -->
      <div id="vanta-background" class="absolute inset-0"></div>
      
      <!-- Film grain overlay only for hero -->
      <div class="absolute inset-0 z-2 pointer-events-none bg-noise opacity-5"></div>
      
      <!-- Hero content -->
      <section class="relative pt-28 pb-20 md:pt-44 md:pb-40 z-10 h-full flex md:items-center items-start">
        <div class="container mx-auto px-4">
          <div class="flex items-center gap-16 md:flex-row flex-col mt-8 md:mt-0">
            <div class="flex-1 md:text-left text-center md:pt-6">
              <h1 class="text-5xl mb-7 font-heading leading-tight drop-shadow-sm">
                <template v-if="selectedLanguage === 'en'">
                  We are a community of <span class="highlight-text">content creators</span> who want to take true ownership of their <span class="highlight-text">data</span> and <span class="highlight-text">IP</span>
                </template>
                <template v-else-if="selectedLanguage === 'pt'">
                  Somos uma comunidade de <span class="highlight-text">criadores</span> que querem ter verdadeira propriedade dos seus <span class="highlight-text">dados</span> e <span class="highlight-text">PI</span>
                </template>
                <template v-else-if="selectedLanguage === 'es'">
                  Somos una comunidad de <span class="highlight-text">creadores de contenido</span> que quieren tener verdadera propiedad de sus <span class="highlight-text">datos</span> y <span class="highlight-text">PI</span>
                </template>
                <template v-else-if="selectedLanguage === 'de'">
                  Wir sind eine Gemeinschaft von <span class="highlight-text">Content-Erstellern</span>, die die wahre Kontrolle über ihre <span class="highlight-text">Daten</span> und <span class="highlight-text">IP</span> übernehmen möchten
                </template>
                <template v-else-if="selectedLanguage === 'fr'">
                  Nous sommes une communauté de <span class="highlight-text">créateurs de contenu</span> qui veulent prendre véritablement possession de leurs <span class="highlight-text">données</span> et <span class="highlight-text">PI</span>
                </template>
              </h1>
              <h2 class="text-2xl mt-7 font-montserrat leading-relaxed">{{ heroSubheading }}</h2>
              <div class="mt-10 flex md:justify-start justify-center">
                <button
                  @click="scrollToHowItWorks"
                  class="bg-gradient-to-br from-purple-600 via-indigo-500 to-blue-600
                         text-white font-montserrat font-bold py-3 px-8 rounded-full shadow-lg
                         transform transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-xl">
                  {{ heroButtonText }}
                </button>
              </div>
            </div>
            <div class="flex-1 flex justify-center items-center pt-8">
              <div class="relative w-full max-w-2xl transform translate-x-4">
                <img src="@/assets/images/hero3.png" alt="Rendery Camera" class="w-full h-auto transform hover:scale-105 transition-transform duration-500 drop-shadow-xl relative" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    
    <!-- SVG Angled Divider -->
    <div class="relative">
      <svg class="absolute bottom-0 left-0 w-full" style="margin-bottom: -1px; transform: translateY(-99px);" height="100" viewBox="0 0 1440 100" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 100L1440 0V100H0Z" fill="#10103c"></path>
      </svg>
    </div>
    
    <!-- Statistics Section -->
    <div class="content-container bg-gradient-to-b from-[#10103c] to-[#1a1a5c]">
      <section class="py-20 relative z-10 overflow-hidden statistics-section">
        <!-- Background pattern -->
        <div class="absolute inset-0 opacity-10">
          <div class="absolute inset-0" style="background-image: radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px); background-size: 50px 50px;"></div>
        </div>
        
        <div class="container mx-auto px-8 relative z-10">
          <div class="max-w-6xl mx-auto">
            <!-- Section Title -->
            <div class="text-center mb-16">
              <h2 class="text-4xl md:text-5xl mb-4 font-heading leading-tight text-white animate-item title-animation" v-html="statisticsTitle">
              </h2>
              <p class="text-xl text-white/80 font-montserrat max-w-2xl mx-auto animate-item subtitle-animation">
                {{ statisticsSubtitle }}
              </p>
            </div>
            
            <!-- Statistics Grid -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
              <!-- Labeled Clips -->
              <div class="text-center animate-item stat-animation" style="--stat-delay: 0">
                <div class="relative mb-6">
                  <div class="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-2xl">
                    <svg class="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V3a1 1 0 011 1v12a1 1 0 01-1 1H8a1 1 0 01-1-1V4m0 0H5a1 1 0 00-1 1v14a1 1 0 001 1h14a1 1 0 001-1V5a1 1 0 00-1-1h-2"/>
                    </svg>
                  </div>

                </div>
                <div class="text-5xl md:text-6xl font-bold text-white mb-2 font-mono">
                  <span ref="clipsCounter">0</span>+
                </div>
                <div class="text-xl text-purple-300 font-montserrat font-semibold mb-2">{{ statisticsItems[0].title }}</div>
                <div class="text-white/70 font-montserrat">{{ statisticsItems[0].description }}</div>
              </div>
              
              <!-- Aggregate Subscribers -->
              <div class="text-center animate-item stat-animation" style="--stat-delay: 1">
                <div class="relative mb-6">
                  <div class="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shadow-2xl">
                    <svg class="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                    </svg>
                  </div>

                </div>
                <div class="text-5xl md:text-6xl font-bold text-white mb-2 font-mono">
                  <span ref="subscribersCounter">0</span>M+
                </div>
                <div class="text-xl text-blue-300 font-montserrat font-semibold mb-2">{{ statisticsItems[1].title }}</div>
                <div class="text-white/70 font-montserrat">{{ statisticsItems[1].description }}</div>
              </div>
              
              <!-- Video Categories -->
              <div class="text-center animate-item stat-animation" style="--stat-delay: 2">
                <div class="relative mb-6">
                  <div class="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-2xl">
                    <svg class="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                    </svg>
                  </div>

                </div>
                <div class="text-5xl md:text-6xl font-bold text-white mb-2 font-mono">
                  <span ref="categoriesCounter">0</span>+
                </div>
                <div class="text-xl text-cyan-300 font-montserrat font-semibold mb-2">{{ statisticsItems[2].title }}</div>
                <div class="text-white/70 font-montserrat">{{ statisticsItems[2].description }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    
    <!-- How it works section -->
    <div class="content-container bg-dark-blue">
      <!-- How it Works Section -->
      <section 
        id="how-it-works" 
        class="py-16 bg-cover bg-center relative z-10 overflow-hidden how-it-works-section" 
        :style="{ 
          backgroundImage: 'url(' + require('@/assets/bg-subtle-dark.svg') + ')',
          boxShadow: 'inset 0 0 50px rgba(0, 0, 0, 0.3)'
        }"
      >
        <div class="container mx-auto px-8">
          <div class="flex flex-col md:flex-row items-center gap-16">
            <!-- Left side - PC image -->
            <div class="md:w-1/2 flex justify-center">
              <div class="relative animate-item image-animation">
                <img 
                  src="@/assets/images/pc.png" 
                  alt="Rendery Platform" 
                  class="rounded-3xl w-full max-w-lg h-auto object-cover shadow-[0_10px_30px_-5px_rgba(0,0,0,0.3),_5px_5px_20px_-5px_rgba(212,175,55,0.25)]"
                />
              </div>
            </div>
            
            <!-- Right side - Steps -->
            <div class="md:w-1/2">
              <h2 class="text-5xl mb-5 font-heading leading-tight text-white animate-item title-animation">{{ howItWorksTitle }}</h2>
              <h3 class="text-2xl mb-10 font-montserrat leading-relaxed text-white/90 animate-item subtitle-animation">{{ howItWorksSubtitle }}</h3>
              
              <div class="space-y-8">
                <!-- Step 1 -->
                <div class="flex items-start gap-6 animate-item step-animation" style="--step-delay: 1">
                  <div class="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-xl font-bold text-white shadow-lg">1</div>
                  <div>
                    <h4 class="text-xl mb-2 font-heading text-white">{{ howItWorksSteps[0].title }}</h4>
                    <p class="text-white/80 leading-relaxed font-montserrat">{{ howItWorksSteps[0].description }}</p>
                  </div>
                </div>
                
                <!-- Step 2 -->
                <div class="flex items-start gap-6 animate-item step-animation" style="--step-delay: 2">
                  <div class="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-xl font-bold text-white shadow-lg">2</div>
                  <div>
                    <h4 class="text-xl mb-2 font-heading text-white">{{ howItWorksSteps[1].title }}</h4>
                    <p class="text-white/80 leading-relaxed font-montserrat">{{ howItWorksSteps[1].description }}</p>
                  </div>
                </div>
                
                <!-- Step 3 -->
                <div class="flex items-start gap-6 animate-item step-animation" style="--step-delay: 3">
                  <div class="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-xl font-bold text-white shadow-lg">3</div>
                  <div>
                    <h4 class="text-xl mb-2 font-heading text-white">{{ howItWorksSteps[2].title }}</h4>
                    <p class="text-white/80 leading-relaxed font-montserrat">{{ howItWorksSteps[2].description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Join Options Section -->
      <section class="py-16 relative z-10 bg-gradient-to-b from-transparent to-purple-900/20 overflow-hidden join-options-section">
        <!-- Background gradient -->
        <div class="absolute inset-0 z-0 join-options-bg"></div>
        
        <div class="container mx-auto px-8 relative z-10">
          <div class="max-w-4xl mx-auto text-center">
            <h2 class="text-5xl mb-5 font-heading leading-tight text-white animate-item title-animation">{{ joinOptionsTitle }}</h2>
            <p class="text-xl mb-14 font-montserrat text-white/90 max-w-2xl mx-auto animate-item subtitle-animation">{{ joinOptionsSubtitle }}</p>
            
            <div class="flex flex-col md:flex-row items-center justify-center gap-10 mt-10">
              <button 
                @click="openSignUpModal"
                class="w-72 px-8 py-4 rounded-full bg-gradient-to-br from-purple-600 via-indigo-500 to-blue-600 text-white font-montserrat font-bold shadow-xl transform transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-2xl flex items-center justify-center gap-3 border-2 border-transparent relative animate-item button-animation"
                style="--button-delay: 1; box-shadow: 0 0 15px rgba(255,255,255,0.3), inset 0 0 0 2px rgba(255,255,255,0.2); background-clip: padding-box;"
              >
                <span class="absolute inset-0 rounded-full overflow-hidden" style="z-index: -1;">
                  <span class="absolute inset-0 rounded-full bg-gradient-to-r from-white/30 via-silver/50 to-white/30 animate-gradient-slow"></span>
                </span>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{{ joinOptionsInvitationButton }}</span>
              </button>
              
              <button 
                @click="openApplyModal"
                class="w-72 px-8 py-4 rounded-full bg-white/10 backdrop-blur-sm text-white font-montserrat font-bold shadow-xl transform transition-all duration-300 ease-in-out hover:scale-105 hover:bg-white/20 hover:shadow-2xl flex items-center justify-center gap-3 border-2 border-transparent relative animate-item button-animation"
                style="--button-delay: 2; box-shadow: 0 0 15px rgba(255,255,255,0.2), inset 0 0 0 2px rgba(255,255,255,0.1); background-clip: padding-box;"
              >
                <span class="absolute inset-0 rounded-full overflow-hidden" style="z-index: -1;">
                  <span class="absolute inset-0 rounded-full bg-gradient-to-r from-white/30 via-silver/50 to-white/30 animate-gradient-slow"></span>
                </span>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                <span>{{ joinOptionsApplyButton }}</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- FAQ Section -->
      <section class="py-20 relative z-10" id="faq">
        <div class="container mx-auto px-4">
          <h2 class="text-4xl text-center mb-12 font-heading text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-white to-purple-200 drop-shadow-md">{{ faqSectionTitle }}</h2>
          
          <!-- Category filters -->
          <div class="flex flex-wrap justify-center gap-3 mb-8">
            <button 
              @click="activeCategory = ''" 
              class="px-4 py-2 rounded-full text-sm font-medium transition-colors duration-300"
              :class="activeCategory === '' ? 'bg-purple-600 text-white' : 'bg-white/20 text-white hover:bg-white/30'"
            >
              {{ allQuestionsText }}
            </button>
            <button 
              v-for="(category, index) in availableLocalizedCategories" 
              :key="category"
              @click="activeCategory = availableCategories[index]" 
              class="px-4 py-2 rounded-full text-sm font-medium transition-colors duration-300"
              :class="activeCategory === availableCategories[index] ? 'bg-purple-600 text-white' : 'bg-white/20 text-white hover:bg-white/30'"
            >
              {{ category }}
            </button>
          </div>
          
          <div class="max-w-3xl mx-auto">
            <transition-group name="fade" tag="div">
              <div v-for="(faq, index) in filteredFaqs" :key="faq.question" class="mb-5 rounded-xl overflow-hidden shadow-sm bg-purple-900/20 backdrop-blur-sm border border-purple-500/20">
                <div @click="toggleFaq(index)" class="flex justify-between items-center p-5 cursor-pointer hover:bg-purple-800/30 transition-colors duration-300">
                  <div class="flex flex-col">
                    <h3 class="text-xl text-white m-0 font-heading">{{ faq.question }}</h3>
                    <span class="text-xs mt-1 px-2 py-1 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white inline-flex items-center self-start">{{ faq.category }}</span>
                  </div>
                  <span class="text-2xl text-white transition-transform duration-300" :class="{ 'rotate-45': faq.open }">+</span>
                </div>
                <div class="overflow-hidden transition-all duration-300" :class="{ 'max-h-0': !faq.open, 'max-h-[500px] px-5 pb-5 overflow-y-auto': faq.open }">
                  <p class="text-white/90 leading-relaxed font-montserrat">{{ faq.answer }}</p>
                </div>
              </div>
            </transition-group>
            
            <div v-if="filteredFaqs.length > initialFaqCount && !showAllFaqs" class="text-center mt-8">
              <button @click="showAllFaqs = true" class="bg-gradient-to-br from-purple-600 via-indigo-500 to-blue-600 text-white font-montserrat font-bold py-2 px-6 rounded-full shadow-lg transform transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-xl">
                {{ showMoreText }}
              </button>
            </div>
            
            <div v-if="showAllFaqs && filteredFaqs.length > initialFaqCount" class="text-center mt-8">
              <button @click="showAllFaqs = false" class="bg-gradient-to-br from-purple-600 via-indigo-500 to-blue-600 text-white font-montserrat font-bold py-2 px-6 rounded-full shadow-lg transform transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-xl">
                {{ showLessText }}
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- AppFooter -->
      <AppFooter />
    </div>
  </div>
  
  <!-- Sign Up Modal -->
  <SignUpModal :show="showSignUpModal" @close="closeSignUpModal" />
  
  <!-- Apply Modal -->
  <ApplyModal />
</template>

<script>
import AppFooter from '@/components/AppFooter.vue';
import SignUpModal from '@/components/SignUpModal.vue';
import ApplyModal from '@/components/ApplyModal.vue';
import faqData from '../../lang.json';
import emitter from '@/eventBus';

export default {
  name: 'Home',
  components: {
    AppFooter,
    SignUpModal,
    ApplyModal
  },
  data() {
    return {
      faqs: [],
      vantaEffect: null,
      initialFaqCount: 5,
      showAllFaqs: false,
      activeCategory: '',
      showSignUpModal: false,
      categories: ['Rights & Ownership', 'Data Usage', 'Payment', 'Privacy & Security'],
      // Statistics animation data
      statisticsAnimated: false,
      counters: {
        clips: 0,
        subscribers: 0,
        categories: 0
      },
      languages: [
        { code: 'en', name: 'English' },
        { code: 'pt', name: 'Português' },
        { code: 'es', name: 'Español' },
        { code: 'de', name: 'Deutsch' },
        { code: 'fr', name: 'Français' }
      ],
      selectedLanguage: 'en',
      uiText: {
        en: { 
          allQuestions: 'All Questions', 
          showMore: 'Show More', 
          showLess: 'Show Less',
          sectionTitle: 'Creator Data Rights FAQ',
          categories: {
            'Rights & Ownership': 'Rights & Ownership',
            'Data Usage': 'Data Usage',
            'Payment': 'Payment',
            'Privacy & Security': 'Privacy & Security'
          }
        },
        pt: { 
          allQuestions: 'Todas as Perguntas', 
          showMore: 'Mostrar Mais', 
          showLess: 'Mostrar Menos',
          sectionTitle: 'FAQ sobre Direitos de Dados de Criadores',
          categories: {
            'Rights & Ownership': 'Direitos e Propriedade',
            'Data Usage': 'Uso de Dados',
            'Payment': 'Pagamento',
            'Privacy & Security': 'Privacidade e Segurança'
          }
        },
        es: { 
          allQuestions: 'Todas las Preguntas', 
          showMore: 'Mostrar Más', 
          showLess: 'Mostrar Menos',
          sectionTitle: 'Preguntas Frecuentes sobre Derechos de Datos del Creador',
          categories: {
            'Rights & Ownership': 'Derechos y Propiedad',
            'Data Usage': 'Uso de Datos',
            'Payment': 'Pago',
            'Privacy & Security': 'Privacidad y Seguridad'
          }
        },
        de: { 
          allQuestions: 'Alle Fragen', 
          showMore: 'Mehr Anzeigen', 
          showLess: 'Weniger Anzeigen',
          sectionTitle: 'FAQ zu Datenrechten von Erstellern',
          categories: {
            'Rights & Ownership': 'Rechte und Eigentum',
            'Data Usage': 'Datennutzung',
            'Payment': 'Zahlung',
            'Privacy & Security': 'Datenschutz und Sicherheit'
          }
        },
        fr: { 
          allQuestions: 'Toutes les Questions', 
          showMore: 'Afficher Plus', 
          showLess: 'Afficher Moins',
          sectionTitle: 'FAQ sur les Droits des Données des Créateurs',
          categories: {
            'Rights & Ownership': 'Droits et Propriété',
            'Data Usage': 'Utilisation des Données',
            'Payment': 'Paiement',
            'Privacy & Security': 'Confidentialité et Sécurité'
          }
        }
      },
      observers: []
    }
  },
  computed: {
    allQuestionsText() {
      return this.uiText[this.selectedLanguage]?.allQuestions || 'All Questions';
    },
    showMoreText() {
      return this.uiText[this.selectedLanguage]?.showMore || 'Show More';
    },
    showLessText() {
      return this.uiText[this.selectedLanguage]?.showLess || 'Show Less';
    },
    localizedCategories() {
      const localizedCats = this.uiText[this.selectedLanguage]?.categories || {};
      return this.categories.map(cat => localizedCats[cat] || cat);
    },
    currentFaqs() {
      return faqData.translations[this.selectedLanguage]?.faqs || faqData.translations['en'].faqs;
    },
    filteredFaqs() {
      const filtered = this.activeCategory === '' 
        ? this.faqs 
        : this.faqs.filter(faq => faq.category === this.activeCategory);
      
      return this.showAllFaqs ? filtered : filtered.slice(0, this.initialFaqCount);
    },
    faqSectionTitle() {
      return this.uiText[this.selectedLanguage]?.sectionTitle || 'Creator Data Rights FAQ';
    },
    heroHeading() {
      return faqData.hero[this.selectedLanguage]?.heading || faqData.hero['en'].heading;
    },
    heroSubheading() {
      return faqData.hero[this.selectedLanguage]?.subheading || faqData.hero['en'].subheading;
    },
    heroButtonText() {
      return faqData.hero[this.selectedLanguage]?.button || faqData.hero['en'].button;
    },
    howItWorksTitle() {
      return faqData.howItWorks[this.selectedLanguage]?.title || faqData.howItWorks['en'].title;
    },
    howItWorksSubtitle() {
      return faqData.howItWorks[this.selectedLanguage]?.subtitle || faqData.howItWorks['en'].subtitle;
    },
    howItWorksSteps() {
      return faqData.howItWorks[this.selectedLanguage]?.steps || faqData.howItWorks['en'].steps;
    },
    joinOptionsTitle() {
      return faqData.joinOptions[this.selectedLanguage]?.title || faqData.joinOptions['en'].title;
    },
    joinOptionsSubtitle() {
      return faqData.joinOptions[this.selectedLanguage]?.subtitle || faqData.joinOptions['en'].subtitle;
    },
    joinOptionsInvitationButton() {
      return faqData.joinOptions[this.selectedLanguage]?.invitationButton || faqData.joinOptions['en'].invitationButton;
    },
    joinOptionsApplyButton() {
      return faqData.joinOptions[this.selectedLanguage]?.applyButton || faqData.joinOptions['en'].applyButton;
    },
    statisticsTitle() {
      return faqData.statistics[this.selectedLanguage]?.title || faqData.statistics['en'].title;
    },
    statisticsSubtitle() {
      return faqData.statistics[this.selectedLanguage]?.subtitle || faqData.statistics['en'].subtitle;
    },
    statisticsItems() {
      return faqData.statistics[this.selectedLanguage]?.items || faqData.statistics['en'].items;
    },
    availableCategories() {
      // Get categories that actually have questions
      const categoriesWithQuestions = new Set();
      this.faqs.forEach(faq => {
        if (faq.category && faq.category !== 'General') {
          categoriesWithQuestions.add(faq.category);
        }
      });
      return this.categories.filter(cat => categoriesWithQuestions.has(cat));
    },
    availableLocalizedCategories() {
      const localizedCats = this.uiText[this.selectedLanguage]?.categories || {};
      return this.availableCategories.map(cat => localizedCats[cat] || cat);
    }
  },
  watch: {
    selectedLanguage() {
      this.loadFaqsForLanguage();
    }
  },
  created() {
    this.loadFaqsForLanguage();
  },
  mounted() {
    const backgroundEl = document.getElementById('vanta-background');
    if (!backgroundEl) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Background element not found!');
      }
      return;
    }

    // Listen for language change events from Navbar
    emitter.on('change-language', (langCode) => {
      this.selectedLanguage = langCode;
    });
    
    // Listen for signup modal open event
    emitter.on('open-signup-modal', () => {
      this.showSignUpModal = true;
    });
    
    // Check for stored language preference
    const storedLang = localStorage.getItem('preferredLanguage');
    if (storedLang && this.languages.some(lang => lang.code === storedLang)) {
      this.selectedLanguage = storedLang;
    }
    
    // Setup Intersection Observer for animations
    this.$nextTick(() => {
      this.setupAnimationObservers();
    });
    
    this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/three.js/r134/three.min.js')
      .then(() => this.loadScript('https://cdn.jsdelivr.net/npm/vanta@0.5.24/dist/vanta.halo.min.js'))
      .then(() => {
        if (typeof window.VANTA === 'undefined') {
          if (process.env.NODE_ENV === 'development') {
            console.error('VANTA is not defined after loading script');
          }
          return;
        }
        
        try {
          this.vantaEffect = window.VANTA.HALO({
            el: backgroundEl,
            mouseControls: true,
            touchControls: true,
            gyroControls: false,
            minHeight: 200.00,
            minWidth: 200.00,
            baseColor: 0x1a59,
            backgroundColor: 0x10103c,
            amplitudeFactor: 1.20,
            size: 1.60,
            xOffset: 0.20,
            yOffset: 0.05,
            speed: 0.20
          });
        } catch (err) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error initializing Vanta effect:', err);
          }
        }
      })
      .catch(error => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to load scripts:', error);
        }
      });
  },
  beforeUnmount() {
    if (this.vantaEffect) {
      this.vantaEffect.destroy();
    }
    
    // Clean up observers
    this.observers.forEach(observer => observer.disconnect());
    
    // Clean up event listeners
    emitter.off('change-language');
    emitter.off('open-signup-modal');
  },
  methods: {
    setupAnimationObservers() {
      const statisticsSection = document.querySelector('.statistics-section');
      const howItWorksSection = document.querySelector('.how-it-works-section');
      const joinOptionsSection = document.querySelector('.join-options-section');
      
      // Statistics section observer with counter animation
      if (statisticsSection) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.statisticsAnimated) {
              entry.target.classList.add('animate-visible');
              this.animateCounters();
              this.statisticsAnimated = true;
              // Unobserve after animation is triggered
              observer.unobserve(entry.target);
            }
          });
        }, {
          threshold: 0.3 // Trigger when 30% of the element is visible
        });
        
        observer.observe(statisticsSection);
        this.observers.push(observer);
      }
      
      if (howItWorksSection) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-visible');
              // Unobserve after animation is triggered
              observer.unobserve(entry.target);
            }
          });
        }, {
          threshold: 0.1 // Trigger when 10% of the element is visible
        });
        
        observer.observe(howItWorksSection);
        this.observers.push(observer);
      }
      
      if (joinOptionsSection) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-visible');
              // Unobserve after animation is triggered
              observer.unobserve(entry.target);
            }
          });
        }, {
          threshold: 0.1 // Trigger when 10% of the element is visible
        });
        
        observer.observe(joinOptionsSection);
        this.observers.push(observer);
      }
    },
    loadFaqsForLanguage() {
      this.faqs = this.categorizeFaqs(this.currentFaqs);
    },
    categorizeFaqs(faqs) {
      // Logic to categorize FAQs based on content and language
      return faqs.map(faq => {
        const question = faq.question.toLowerCase();
        let category = 'General';
        
        // English keywords
        if (this.selectedLanguage === 'en') {
          if (question.includes('right') || question.includes('ownership') || question.includes('intellectual property') || 
              question.includes('retain') || question.includes('future')) {
            category = 'Rights & Ownership';
          } else if (question.includes('use') || question.includes('buyer') || question.includes('voice') || 
                     question.includes('image') || question.includes('synthesis') || question.includes('replicated')) {
            category = 'Data Usage';
          } else if (question.includes('revenue') || question.includes('payment') || question.includes('profit') || 
                     question.includes('transaction')) {
            category = 'Payment';
          } else if (question.includes('privacy') || question.includes('protection') || question.includes('security') || 
                     question.includes('gdpr') || question.includes('lgpd') || question.includes('deepfake')) {
            category = 'Privacy & Security';
          }
        }
        // Portuguese keywords
        else if (this.selectedLanguage === 'pt') {
          if (question.includes('direito') || question.includes('propriedade') || question.includes('intelectual') || 
              question.includes('manter') || question.includes('futuro')) {
            category = 'Rights & Ownership';
          } else if (question.includes('uso') || question.includes('comprador') || question.includes('voz') || 
                     question.includes('imagem') || question.includes('síntese') || question.includes('replicada')) {
            category = 'Data Usage';
          } else if (question.includes('receita') || question.includes('pagamento') || question.includes('lucro') || 
                     question.includes('transação')) {
            category = 'Payment';
          } else if (question.includes('privacidade') || question.includes('proteção') || question.includes('segurança') || 
                     question.includes('gdpr') || question.includes('lgpd') || question.includes('deepfake')) {
            category = 'Privacy & Security';
          }
        }
        // Spanish keywords
        else if (this.selectedLanguage === 'es') {
          if (question.includes('derecho') || question.includes('propiedad') || question.includes('intelectual') || 
              question.includes('conservar') || question.includes('futuro')) {
            category = 'Rights & Ownership';
          } else if (question.includes('uso') || question.includes('comprador') || question.includes('voz') || 
                     question.includes('imagen') || question.includes('síntesis') || question.includes('replicada')) {
            category = 'Data Usage';
          } else if (question.includes('ingreso') || question.includes('pago') || question.includes('ganancia') || 
                     question.includes('transacción')) {
            category = 'Payment';
          } else if (question.includes('privacidad') || question.includes('protección') || question.includes('seguridad') || 
                     question.includes('gdpr') || question.includes('lgpd') || question.includes('deepfake')) {
            category = 'Privacy & Security';
          }
        }
        // German keywords
        else if (this.selectedLanguage === 'de') {
          if (question.includes('recht') || question.includes('eigentum') || question.includes('geistig') || 
              question.includes('behalten') || question.includes('zukunft')) {
            category = 'Rights & Ownership';
          } else if (question.includes('verwendung') || question.includes('käufer') || question.includes('stimme') || 
                     question.includes('bild') || question.includes('synthese') || question.includes('repliziert')) {
            category = 'Data Usage';
          } else if (question.includes('einnahmen') || question.includes('zahlung') || question.includes('gewinn') || 
                     question.includes('transaktion')) {
            category = 'Payment';
          } else if (question.includes('privatsphäre') || question.includes('schutz') || question.includes('sicherheit') || 
                     question.includes('dsgvo') || question.includes('lgpd') || question.includes('deepfake')) {
            category = 'Privacy & Security';
          }
        }
        // French keywords
        else if (this.selectedLanguage === 'fr') {
          if (question.includes('droit') || question.includes('propriété') || question.includes('intellectuelle') || 
              question.includes('conserver') || question.includes('futur')) {
            category = 'Rights & Ownership';
          } else if (question.includes('utilisation') || question.includes('acheteur') || question.includes('voix') || 
                     question.includes('image') || question.includes('synthèse') || question.includes('répliqué')) {
            category = 'Data Usage';
          } else if (question.includes('revenu') || question.includes('paiement') || question.includes('profit') || 
                     question.includes('transaction')) {
            category = 'Payment';
          } else if (question.includes('confidentialité') || question.includes('protection') || question.includes('sécurité') || 
                     question.includes('rgpd') || question.includes('lgpd') || question.includes('deepfake')) {
            category = 'Privacy & Security';
          }
        }
        // Default to English for any other language
        else {
          if (question.includes('right') || question.includes('ownership') || question.includes('intellectual property') || 
              question.includes('retain') || question.includes('future')) {
            category = 'Rights & Ownership';
          } else if (question.includes('use') || question.includes('buyer') || question.includes('voice') || 
                     question.includes('image') || question.includes('synthesis') || question.includes('replicated')) {
            category = 'Data Usage';
          } else if (question.includes('revenue') || question.includes('payment') || question.includes('profit') || 
                     question.includes('transaction')) {
            category = 'Payment';
          } else if (question.includes('privacy') || question.includes('protection') || question.includes('security') || 
                     question.includes('gdpr') || question.includes('lgpd') || question.includes('deepfake')) {
            category = 'Privacy & Security';
          }
        }
        
        return {
          ...faq,
          open: false,
          category
        };
      });
    },
    toggleFaq(index) {
      const faq = this.filteredFaqs[index];
      if (faq) {
        const faqIndex = this.faqs.findIndex(f => f.question === faq.question);
        if (faqIndex !== -1) {
          this.faqs[faqIndex].open = !this.faqs[faqIndex].open;
        }
      }
    },
    loadScript(src) {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }
        
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve();
        script.onerror = (e) => reject(e);
        document.head.appendChild(script);
      });
    },
    scrollToHowItWorks() {
      const target = document.getElementById('how-it-works');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
      }
    },
    openSignUpModal() {
      this.showSignUpModal = true;
    },
    closeSignUpModal() {
      this.showSignUpModal = false;
    },
    openApplyModal() {
      emitter.emit('open-apply-modal');
    },
    animateCounters() {
      // Sequential animation with delays - slower animation
      setTimeout(() => this.animateCounter('clips', 1000, 2000), 0);
      setTimeout(() => this.animateCounter('subscribers', 20, 2000), 600);
      setTimeout(() => this.animateCounter('categories', 40, 2000), 1200);
    },
    animateCounter(type, target, duration) {
      const startTime = performance.now();
      const startValue = 0;
      
      const animate = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Use easeOutQuart for smooth deceleration
        const easedProgress = this.easeOutQuart(progress);
        const currentValue = Math.floor(startValue + (target - startValue) * easedProgress);
        
        // Update the counter value
        this.counters[type] = currentValue;
        
        // Update the DOM element
        const element = this.$refs[`${type}Counter`];
        if (element) {
          element.textContent = currentValue;
        }
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // Ensure final value is set
          this.counters[type] = target;
          if (element) {
            element.textContent = target;
          }
        }
      };
      
      requestAnimationFrame(animate);
    },
    easeOutQuart(t) {
      return 1 - Math.pow(1 - t, 4);
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&family=Playfair+Display:wght@400;500;600;700&display=swap');

@font-face {
  font-family: 'Philosopher';
  src: url('@/assets/fonts/Philosopher-Regular.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
}

.font-philosopher { font-family: 'Philosopher', sans-serif; }
.font-montserrat { font-family: 'Montserrat', sans-serif; }
.font-heading { font-family: 'Playfair Display', serif; }
.text-primary { color: theme('colors.purple.600'); }

/* Transition animations */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.5s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Custom animations */
@keyframes pulse-slow {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.85;
  }
}

@keyframes gradient-slow {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes highlight-sweep {
  0% {
    opacity: 0;
    transform: translateX(-100%);
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 0;
    transform: translateX(100%);
  }
}

.highlight-sweep {
  animation: highlight-sweep 3.5s ease-in-out infinite;
  animation-delay: 1s;
}

.animate-pulse-slow {
  animation: pulse-slow 3s ease-in-out infinite;
}

.animate-gradient-slow {
  animation: gradient-slow 3s ease infinite;
  background-size: 200% 200%;
}

.highlight-text {
  position: relative;
  display: inline;
  z-index: 1;
  color: #000;
  font-weight: 700;
  padding: 0 4px;
  border-radius: 3px;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
  background-color: #FFEB3B;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  text-shadow: none;
}

.bg-noise {
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: 200px 200px;
}

.hero-container {
  height: 100vh;
  background-color: transparent;
  position: relative;
  overflow: hidden;
}

#vanta-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.content-container {
  background-color: #10103c;
  position: relative;
  z-index: 10;
  padding-top: 60px;
}

.bg-dark-blue { background-color: #10103c; }
.bg-dark-blue-light { background-color: #191956; }

.hero-container .container,
.hero-container section,
.hero-container div {
  background-color: transparent !important;
}

h1, h2, h3, p, label, .container {
  color: white;
  text-shadow: 0px 1px 3px rgba(0,0,0,0.3);
}

.bg-white\/80, .backdrop-blur-sm {
  background-color: rgba(255, 255, 255, 0.15) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.bg-white\/80 h3 { color: white; }
.bg-white\/80 p { color: rgba(255, 255, 255, 0.8); }

input, textarea {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  color: white;
}

input::placeholder, textarea::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

img[src*="hero3.png"] {
  filter: drop-shadow(0 10px 15px rgba(0, 0, 0, 0.2));
  background: transparent;
}

.home .fixed:not(header) {
  position: relative !important;
}

/* Join Options background */
.join-options-bg {
  background: linear-gradient(135deg, #10103c 0%, #1e1e5b 100%);
  opacity: 0.9;
}

/* Animation classes for How it Works section */
.animate-item {
  opacity: 0;
  transform: translateY(20px);
}

/* Only apply animations when the section is visible */
.animate-visible .title-animation {
  animation: fade-in-up 0.7s ease-out 0.1s forwards;
}

.animate-visible .subtitle-animation {
  animation: fade-in-up 0.7s ease-out 0.3s forwards;
}

.animate-visible .image-animation {
  animation: fade-in-up 0.7s ease-out 0.5s forwards;
}

.animate-visible .step-animation {
  animation: fade-in-up 0.7s ease-out calc(0.7s + (var(--step-delay) * 0.2s)) forwards;
}

@keyframes fade-in-up {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-visible .button-animation {
  animation: fade-in-up 0.7s ease-out calc(0.7s + (var(--button-delay) * 0.2s)) forwards;
}

/* Statistics Section Animations */
.statistics-section .animate-item {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.statistics-section.animate-visible .animate-item {
  opacity: 1;
  transform: translateY(0);
}

.statistics-section .title-animation {
  transition-delay: 0.1s;
}

.statistics-section .subtitle-animation {
  transition-delay: 0.2s;
}

.statistics-section .stat-animation {
  transition-delay: calc(0.3s + var(--stat-delay) * 0.2s);
}

/* Counter number styling */
.statistics-section .font-heading span {
  display: inline-block;
  transition: all 0.3s ease;
}

/* Background pattern animation */
@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

.statistics-section .absolute.inset-0 {
  animation: float 6s ease-in-out infinite;
}

/* Gradient animation for icons */
@keyframes gradient-shift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.statistics-section .bg-gradient-to-br {
  background-size: 200% 200%;
  animation: gradient-shift 4s ease infinite;
}
</style> 